import { errorWords } from "@alm/domain";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { SearchField } from "@/components/app/patterns/search-field";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { DataTable } from "@/components/app/patterns/data-table";
import { EmptyState } from "@/components/app/lists/empty-state";
import { OverflowText } from "@/components/app/lists/overflow-text";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { searchPatch, optionalSearch } from "@/lib/search";
import { orpc, type Output, type orpcClient } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { OrganizationForm } from "./-components/organization-form";

export const Route = createFileRoute("/_authenticated/_administration/settings/plans-modules/")({
  validateSearch: (search: Record<string, unknown>): { q?: string } => ({
    q: optionalSearch(search.q),
  }),
  component: OrganizationsPage,
});

type Organization = Output<typeof orpcClient.administration.organizations.list>["rows"][number];

const COLUMNS: ColumnDef<Organization>[] = [
  {
    id: "organization",
    header: "Organization",
    meta: { className: "font-medium" },
    cell: ({ row: { original: org } }) => (
      <OverflowText tip={org.name}>
        <Link
          to="/settings/plans-modules/$organizationId"
          params={{ organizationId: org.id }}
          className="hover:underline"
        >
          {org.name}
        </Link>
      </OverflowText>
    ),
  },
  {
    id: "slug",
    header: "Slug",
    meta: { className: "font-mono text-xs" },
    cell: ({ row: { original: org } }) => (
      <OverflowText lines={1} width="sm">
        {org.slug}
      </OverflowText>
    ),
  },
  {
    id: "plan",
    header: "Plan",
    cell: ({ row: { original: org } }) => (
      <span className="flex flex-wrap items-center gap-1">
        {org.planName ?? <span className="text-muted-foreground">No plan</span>}
        {org.addonsRequested > 0 ? (
          <Badge variant="warn">{org.addonsRequested} AI requested</Badge>
        ) : null}
      </span>
    ),
  },
  {
    id: "facilities",
    header: "Facilities",
    meta: { align: "right" },
    cell: ({ row: { original: org } }) => org.facilityCount,
  },
  {
    id: "status",
    header: "Status",
    cell: ({ row: { original: org } }) => (
      <Badge variant={org.status === "active" ? "ok" : "neutral"} className="capitalize">
        {org.status}
      </Badge>
    ),
  },
];

function OrganizationsPage() {
  const { q } = Route.useSearch();
  const patch = searchPatch(useNavigate({ from: Route.fullPath }));
  const {
    text: search,
    change: setSearch,
    settling,
  } = useSearchParamText(q, (next) => patch({ q: next }));
  const [creating, setCreating] = useState(false);
  const canManage = useCan("platform:manage");
  const queryClient = useQueryClient();
  const list = useQuery({
    ...orpc.administration.organizations.list.queryOptions({
      input: { search: q || undefined, limit: 100, offset: 0 },
    }),
    placeholderData: keepPreviousData,
  });
  const searching = settling || list.isPlaceholderData;
  const create = useMutation(
    orpc.administration.organizations.create.mutationOptions({
      onSuccess: async () => {
        toast.success("Organization added");
        setCreating(false);
        await queryClient.invalidateQueries({ queryKey: orpc.administration.organizations.key() });
      },
      onError: (error) => toast.error(errorWords(error)),
    }),
  );
  const rows = list.data?.rows ?? [];
  return (
    <>
      <PageHeader
        title="Plans & modules"
        meta="Every organization on the platform and the plan it is on. Open one to change its modules."
        actions={
          <>
            <Button variant="outline" asChild>
              <Link to="/settings/plans-modules/catalog">Open the catalog</Link>
            </Button>
            {canManage ? <Button onClick={() => setCreating(true)}>Add organization</Button> : null}
          </>
        }
      />
      <SearchField value={search} onChange={setSearch} placeholder="Search organizations" />
      {list.isError ? (
        <ReadFailed
          message="Could not load organizations."
          error={list.error}
          retrying={list.isFetching}
          onRetry={() => void list.refetch()}
        />
      ) : (
        <DataTable
          data={rows}
          columns={COLUMNS}
          noun="organizations"
          rowId={(org) => org.id}
          pending={list.isPending || (rows.length === 0 && searching)}
          busy={searching}
          empty={
            <EmptyState
              title={q ? `No organization matches “${q}”` : "No organizations yet"}
              action={
                q ? (
                  <Button variant="outline" onClick={() => setSearch("")}>
                    Clear search
                  </Button>
                ) : null
              }
            />
          }
        />
      )}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add organization</DialogTitle>
          </DialogHeader>
          <OrganizationForm
            pending={create.isPending}
            onCancel={() => setCreating(false)}
            onSubmit={(values) => create.mutate(values)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
